"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import useStore from "@/store/useStore";
import Button from "@/components/ui/button";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { toast } from "@/hooks/use-toast";

export default function LandingPage() {
  const router = useRouter();
  const { signInWithProvider, signUp, session } = useStore();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      const { success, error } = await signInWithProvider("google", {
        redirectTo: `${process.env.NEXT_PUBLIC_APP_URL}/auth/callback`,
      });

      if (!success) {
        throw new Error(error || "Failed to sign up with Google");
      }
    } catch (error) {
      console.error("Google sign up error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to sign up with Google",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    if (!email) return;

    // Redirect to onboarding with the email
    router.push(`/onboarding?email=${encodeURIComponent(email)}`);
  };

  return (
    <div
      className="min-h-screen bg-white"
      style={{ minHeight: "var(--dynamic-vh, 100vh)" }}
    >
      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                "@id": "https://qensa.me/#software",
                name: "Qensa",
                url: "https://qensa.me",
                description:
                  "No-code website builder for creating personal websites, portfolios, and digital spaces with drag-and-drop simplicity",
                applicationCategory: "WebApplication",
                operatingSystem: "Web Browser",
                softwareVersion: "2.0",
                author: {
                  "@type": "Organization",
                  name: "Qensa",
                  url: "https://qensa.me",
                },
                offers: {
                  "@type": "Offer",
                  price: "5.00",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  priceValidUntil: "2025-12-31",
                  eligibleRegion: "US",
                },
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: "4.8",
                  ratingCount: "150",
                  bestRating: "5",
                },
                featureList: [
                  "Drag and Drop Website Builder",
                  "No Coding Required",
                  "Responsive Design",
                  "Custom Widgets",
                  "Portfolio Templates",
                  "SEO Optimization",
                  "Social Media Integration",
                  "File Sharing",
                  "Image Galleries",
                ],
              },
              {
                "@type": "Organization",
                "@id": "https://qensa.me/#organization",
                name: "Qensa",
                url: "https://qensa.me",
                logo: "https://qensa.me/logos/purple.svg",
                description:
                  "Empowering individuals and businesses to create beautiful websites without coding",
                foundingDate: "2024",
                founder: {
                  "@type": "Person",
                  name: "Qensa Team",
                },
                sameAs: ["https://twitter.com/qensa"],
                contactPoint: {
                  "@type": "ContactPoint",
                  contactType: "customer service",
                  availableLanguage: "English",
                },
              },
              {
                "@type": "WebSite",
                "@id": "https://qensa.me/#website",
                url: "https://qensa.me",
                name: "Qensa - No-Code Website Builder",
                description:
                  "Create stunning personal websites and portfolios with our drag-and-drop website builder",
                publisher: {
                  "@id": "https://qensa.me/#organization",
                },
                potentialAction: {
                  "@type": "SearchAction",
                  target: {
                    "@type": "EntryPoint",
                    urlTemplate:
                      "https://qensa.me/qonnect?search={search_term_string}",
                  },
                  "query-input": "required name=search_term_string",
                },
              },
              {
                "@type": "Product",
                "@id": "https://qensa.me/#product",
                name: "Qensa Website Builder",
                description:
                  "Professional no-code website builder for personal websites, portfolios, and digital spaces",
                brand: {
                  "@id": "https://qensa.me/#organization",
                },
                category: "Website Builder Software",
                offers: {
                  "@type": "Offer",
                  price: "5.00",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  seller: {
                    "@id": "https://qensa.me/#organization",
                  },
                },
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: "4.8",
                  ratingCount: "150",
                },
              },
            ],
          }),
        }}
      />

      {/* Header */}
      <header className="flex items-center justify-between px-4 sm:px-8 py-4 sm:py-6">
        <div className="flex items-center">
          <Image
            src="/logos/beta/purple.svg"
            alt="Qensa Logo"
            width={100}
            height={25}
            className="sm:w-[140px] sm:h-[35px]"
            priority
          />
        </div>

        <div className="flex items-center space-x-3 sm:space-x-8">
          <Link
            href="/qonnect"
            className="text-primary hover:text-primary/80 transition-colors font-medium text-xs sm:text-base"
          >
            Explore
          </Link>
          <Link
            href="/signin"
            className="border border-primary text-primary px-3 sm:px-6 py-1.5 sm:py-2 rounded-lg hover:bg-primary hover:text-white transition-colors font-medium text-xs sm:text-base"
          >
            Sign in
          </Link>
        </div>
      </header>

      {/* Main Splash Section - Made responsive */}
      <div className="px-4 sm:px-8 py-6 sm:py-12 min-h-[60vh] flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-32">
            {/* Left Column - Hero Content */}
            <div className="flex-1 text-center lg:text-left">
              <div className="max-w-xl mx-auto lg:mx-0">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                  No-Code Website Builder: Create Your Digital Space in Minutes.
                </h1>

                <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-4">
                  Build stunning personal websites, portfolios, and online
                  spaces with our intuitive drag-and-drop website builder. No
                  coding skills required, no complex setup, no hidden fees.
                </p>
              </div>
            </div>

            {/* Right Column - Sign Up Form */}
            <div className="w-full max-w-sm lg:w-96 space-y-4 sm:space-y-6">
              <Button
                variant="google"
                icon={faGoogle}
                onClick={handleGoogleSignUp}
                fullWidth
                disabled={loading}
                className="!bg-primary !text-white hover:!bg-primary/90 !py-3 sm:!py-4 !rounded-2xl !text-base sm:!text-lg !font-medium"
              >
                {loading ? "Connecting..." : "Sign up with Google"}
              </Button>

              <div className="text-center text-gray-500 font-medium">Or</div>

              <form
                onSubmit={handleEmailSignUp}
                className="space-y-3 sm:space-y-4"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-100 text-gray-900 rounded-2xl px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg placeholder:text-gray-500"
                  placeholder="email address"
                  required
                />
                <button
                  type="submit"
                  className="w-full bg-black text-white py-3 sm:py-4 rounded-2xl hover:bg-gray-800 transition-colors font-medium text-base sm:text-lg"
                >
                  Create your Qensa
                </button>
              </form>

              <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                By clicking Create account, you agree to Qensa's{" "}
                <Link
                  href="https://www.qensa.com/terms-and-conditions"
                  className="text-primary hover:text-primary/80 hover:underline"
                >
                  Terms and Conditions
                </Link>{" "}
                and confirm you have read our{" "}
                <Link
                  href="https://www.qensa.com/privacy-policy"
                  className="text-primary hover:text-primary/80 hover:underline"
                >
                  Privacy Notice
                </Link>
                . You may receive offers, news and updates from us.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Pages Section - Improved mobile spacing */}
      <div className="px-4 sm:px-8 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Beautiful Website Examples: Portfolio Inspiration Gallery
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover stunning personal websites, creative portfolios, and
              professional digital spaces created with Qensa's no-code website
              builder
            </p>
          </div>

          {/* Featured Pages Masonry Grid */}
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 sm:gap-6 space-y-4 sm:space-y-6">
            {/* Joseph */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Joseph
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/Joseph.png"
                  alt="Joseph"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>

            {/* Artist */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Artist
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/Artist.png"
                  alt="Artist"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>

            {/* Product Designer */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Product Designer
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/Product Designer.png"
                  alt="Product Designer"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>

            {/* NFT Project */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  NFT Project
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/NFT Project.png"
                  alt="NFT Project"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>

            {/* Individual */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Individual
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/Individual.png"
                  alt="Individual"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>

            {/* Real Estate Agent */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Real Estate Agent
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/Real Estate Agent.png"
                  alt="Real Estate Agent"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>

            {/* Photographer */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Photographer
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/Photographer.png"
                  alt="Photographer"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>

            {/* Joel */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Joel
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/Joel.png"
                  alt="Joel"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>

            {/* Creative Director */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Creative Director
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/Creative Director.png"
                  alt="Creative Director"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>

            {/* Charity Project */}
            <div className="break-inside-avoid">
              <div className="p-2 text-center">
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Charity Project
                </span>
              </div>
              <div className="relative">
                <img
                  src="/images/featured/Charity Project.png"
                  alt="Charity Project"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Widgets Section - Improved mobile grid */}
      <div className="px-4 sm:px-8 py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Professional Website Components: Drag & Drop Widgets
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Build your perfect website with our comprehensive widget library.
              Add bio sections, image galleries, social links, file uploads, and
              more with simple drag-and-drop functionality.
            </p>
          </div>

          {/* Widgets Grid - Better mobile layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {/* Bio Widget */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Bio</h3>
              <p className="text-sm text-gray-600">
                Tell your story with a beautiful bio section
              </p>
            </div>

            {/* Links Widget */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Links</h3>
              <p className="text-sm text-gray-600">
                Share your social media and important links
              </p>
            </div>

            {/* Media Widget */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Media</h3>
              <p className="text-sm text-gray-600">
                Showcase your photos, videos, and files
              </p>
            </div>

            {/* Content Widget */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Content</h3>
              <p className="text-sm text-gray-600">
                Add rich text content with formatting
              </p>
            </div>

            {/* YouTube Widget */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative">
              <div className="absolute top-3 right-3">
                <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                  Smart
                </span>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-red-600"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">YouTube</h3>
              <p className="text-sm text-gray-600">
                Embed videos automatically with smart detection
              </p>
            </div>

            {/* File Widget */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Files</h3>
              <p className="text-sm text-gray-600">
                Upload and share documents, PDFs, and more
              </p>
            </div>

            {/* Image Gallery Widget */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Image Gallery
              </h3>
              <p className="text-sm text-gray-600">
                Create beautiful photo galleries and portfolios
              </p>
            </div>

            {/* Spotify Widget - Coming Soon */}
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 transition-shadow relative opacity-60">
              <div className="absolute top-3 right-3">
                <span className="bg-gray-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                  Coming Soon
                </span>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-500 mb-2">Spotify</h3>
              <p className="text-sm text-gray-400">
                Showcase your music and playlists
              </p>
            </div>

            {/* Placeholder Widget 1 - Blurred */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 transition-shadow relative opacity-40 blur-sm">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 rounded"></div>
              </div>
              <h3 className="font-semibold text-gray-400 mb-2">Widget</h3>
              <p className="text-sm text-gray-300">Future development</p>
            </div>

            {/* Placeholder Widget 2 - Blurred - Hidden on mobile */}
            <div className="hidden sm:block bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 transition-shadow relative opacity-40 blur-sm">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 rounded"></div>
              </div>
              <h3 className="font-semibold text-gray-400 mb-2">Widget</h3>
              <p className="text-sm text-gray-300">Future development</p>
            </div>

            {/* Placeholder Widget 3 - Blurred - Hidden on mobile */}
            <div className="hidden lg:block bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 transition-shadow relative opacity-40 blur-sm">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 rounded"></div>
              </div>
              <h3 className="font-semibold text-gray-400 mb-2">Widget</h3>
              <p className="text-sm text-gray-300">Future development</p>
            </div>

            {/* Placeholder Widget 4 - Blurred - Hidden on mobile */}
            <div className="hidden xl:block bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 transition-shadow relative opacity-40 blur-sm">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 rounded"></div>
              </div>
              <h3 className="font-semibold text-gray-400 mb-2">Widget</h3>
              <p className="text-sm text-gray-300">Future development</p>
            </div>
          </div>

          {/* Call to Action - Better mobile spacing */}
          <div className="text-center mt-8 sm:mt-12">
            <p className="text-gray-600 mb-4 sm:mb-6">
              And many more widgets coming every week!
            </p>
            <div className="flex justify-center px-4">
              <Button
                onClick={() => router.push("/onboarding")}
                className="!bg-primary !text-white hover:!bg-primary/90 !py-3 sm:!py-4 !px-6 sm:!px-8 !rounded-2xl !text-base sm:!text-lg !font-medium w-full sm:w-auto max-w-sm"
              >
                Start Building Your Website Free
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#06060f] text-white py-12 px-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col space-y-8">
            {/* Top section with nav links and contact */}
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="flex space-x-8">
                <Link
                  href="/qonnect"
                  className="text-white/80 hover:text-accent-lime transition-colors text-lg"
                >
                  Qonnect
                </Link>
                <Link
                  href="/faq"
                  className="text-white/80 hover:text-accent-lime transition-colors text-lg"
                >
                  FAQ
                </Link>
              </div>
              <div className="text-right">
                <div className="text-4xl text-white/80 font-thin">
                  contact@qensa.com
                </div>
              </div>
            </div>

            {/* Middle section with logo and social links */}
            <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
              {/* Logo section */}
              <div className="text-center md:text-left md:ml-0 md:pl-0">
                <div className="text-8xl font-bold text-primary mb-2 ml-0 pl-0">
                  Qensa
                </div>
                <div className="text-lg text-white/60">Qensa Inc. 2025</div>
              </div>

              {/* Social links */}
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/qensa.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative block w-8 h-8 hover:opacity-80 transition-opacity"
                >
                  <img
                    src="https://cdn.prod.website-files.com/63e915d31eb4add4eced9d5b/677060ce3937ff6a6b19f41b_IG.png"
                    alt="Instagram"
                    className="w-full h-full object-contain"
                  />
                </a>
                <a
                  href="https://bsky.app/profile/qensa.bsky.social"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative block w-8 h-8 hover:opacity-80 transition-opacity"
                >
                  <img
                    src="https://cdn.prod.website-files.com/63e915d31eb4add4eced9d5b/6876b282b0795e46a41dd0db_Bluesky_Logo%201.png"
                    alt="Bluesky"
                    className="w-full h-full object-contain"
                  />
                </a>
                <a
                  href="https://x.com/qensa_me"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative block w-8 h-8 hover:opacity-80 transition-opacity"
                >
                  <img
                    src="https://cdn.prod.website-files.com/63e915d31eb4add4eced9d5b/6770613fefed8e06dc809583_Twitter.png"
                    alt="Twitter"
                    className="w-full h-full object-contain"
                  />
                </a>
                <a
                  href="https://www.linkedin.com/company/qensa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative block w-8 h-8 hover:opacity-80 transition-opacity"
                >
                  <img
                    src="https://cdn.prod.website-files.com/63e915d31eb4add4eced9d5b/68783d4d0e481e335f951709_linkedin.png"
                    alt="LinkedIn"
                    className="w-full h-full object-contain"
                  />
                </a>
              </div>
            </div>

            {/* Bottom section with legal links */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 pt-4 border-t border-white/10">
              <Link
                href="https://www.qensa.com/terms-and-conditions"
                className="text-white/60 hover:text-accent-lime transition-colors text-sm"
              >
                Terms and conditions
              </Link>
              <Link
                href="https://www.qensa.com/privacy-policy"
                className="text-white/60 hover:text-accent-lime transition-colors text-sm"
              >
                Privacy policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
